import { useQuery } from '@tanstack/react-query';
import {
  CONNECTOR_DEVICE_COLUMNS,
  CONNECTOR_ROOT_COLUMNS,
  SCAN_MAX_DEPTH,
  parseFolderBindings,
  parsePendingPairing,
} from '@cerebro-diem/core';
import type { BindableKind, ConnectorDevice, ConnectorRoot, PendingPairing } from '@cerebro-diem/core';
import { supabase } from './supabase';
import { queryKeys } from './queries';

/**
 * The signed-in user's connectors, newest first, each with the key the
 * computer that was paired holds. No key material is asked for: a connector is
 * named by its key's fingerprint, which the computer shows too.
 */
export function useConnectors() {
  return useQuery({
    queryKey: queryKeys.connectors,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connector_device_status')
        .select(CONNECTOR_DEVICE_COLUMNS)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ConnectorDevice[];
    },
  });
}

/**
 * What the computer waiting under `userCode` claims about itself, so the
 * person approving can compare it with the machine in front of them. Nothing
 * is bound to the account by looking.
 */
export async function lookupPairing(userCode: string): Promise<PendingPairing> {
  const code = userCode.trim().toUpperCase();
  if (!/^[0-9A-HJKMNP-TVWXYZ]{4}-?[0-9A-HJKMNP-TVWXYZ]{4}$/.test(code)) {
    throw new Error('that is not a pairing code; it is eight characters, as the computer shows it');
  }
  const { data, error } = await supabase.rpc('connector_pairing_pending', {
    p_user_code: code.includes('-') ? code : `${code.slice(0, 4)}-${code.slice(4)}`,
  });
  if (error) throw error;
  return parsePendingPairing(data);
}

/**
 * Approve a pairing. This is the one act that binds that computer's key to
 * this account. With `connectorId`, the key replaces the one an existing
 * connector held, so its folders and links carry over and the old key dies.
 */
export async function approvePairing(userCode: string, name: string, connectorId: string | null): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('a connector needs a name');
  const { error } = await supabase.rpc('connector_pairing_approve', {
    p_user_code: userCode,
    p_name: trimmed,
    p_connector_id: connectorId,
  });
  if (error) throw error;
}

/** Deny a pairing: the computer is told, and nothing of its key is ever stored. */
export async function denyPairing(userCode: string): Promise<void> {
  const { error } = await supabase.rpc('connector_pairing_deny', { p_user_code: userCode });
  if (error) throw error;
}

/** Every root the user's connectors offered, with its latest scans. */
export function useConnectorRoots() {
  return useQuery({
    queryKey: queryKeys.connectorRoots,
    queryFn: async () => {
      const { data, error } = await supabase.from('connector_root_status').select(CONNECTOR_ROOT_COLUMNS).order('root_key');
      if (error) throw error;
      return (data ?? []) as ConnectorRoot[];
    },
  });
}

/** Update one of the user's rows; throws when nothing was updated, so a silent no-op is never shown as done. */
async function updateOne(table: 'connectors' | 'connector_roots', match: Record<string, string>, update: Record<string, unknown>) {
  let query = supabase.from(table).update(update);
  for (const [column, value] of Object.entries(match)) query = query.eq(column, value);
  const { data, error } = await query.select(table === 'connectors' ? 'id' : 'root_key');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('not found; it may have been removed');
}

export function renameConnector(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return Promise.reject(new Error('a connector needs a name'));
  return updateOne('connectors', { id }, { name: trimmed });
}

/** Revoke: the database stamps the time, refuses the connector's next call, and never undoes it. */
export function revokeConnector(id: string): Promise<void> {
  return updateOne('connectors', { id }, { revoked_at: new Date().toISOString() });
}

/** Approve a root (its scans are accepted from now on) or withdraw approval (they are refused, and its listing is dropped). */
export function setRootApproved(connectorId: string, rootKey: string, approved: boolean): Promise<void> {
  return updateOne('connector_roots', { connector_id: connectorId, root_key: rootKey }, { approved_at: approved ? new Date().toISOString() : null });
}

/** An entity's linked folders with their listings (folder_binding_listing). */
export function useFolderBindings(kind: BindableKind, entityId: string) {
  return useQuery({
    queryKey: queryKeys.folderBindings(kind, entityId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('folder_binding_listing', { p_entity_kind: kind, p_entity_id: entityId });
      if (error) throw error;
      return parseFolderBindings(data);
    },
  });
}

export interface PickerFolder {
  stable_id: string;
  name: string;
  depth: number;
}

/**
 * The folders directly in `parent` (null: at the top of the root) in the
 * root's latest good scan, with ids from that scan's runtime, by name. Only
 * folders whose contents a scan lists: one at the last level cannot be linked.
 */
export function usePickerFolders(root: ConnectorRoot | null, parent: string | null) {
  return useQuery({
    queryKey: queryKeys.pickerFolders(root?.connector_id ?? '', root?.root_key ?? '', root?.last_ok_scan_at ?? '', parent),
    enabled: root !== null && root.last_ok_scan_at !== null,
    queryFn: async () => {
      if (!root?.last_ok_scan_at) return [];
      let query = supabase
        .from('folder_entries')
        .select('stable_id, name, depth')
        .eq('connector_id', root.connector_id)
        .eq('root_key', root.root_key)
        .eq('runtime', root.last_ok_scan_runtime ?? '')
        .eq('kind', 'dir')
        .eq('last_seen_scan_at', root.last_ok_scan_at)
        .lt('depth', SCAN_MAX_DEPTH);
      query = parent === null ? query.is('parent_stable_id', null) : query.eq('parent_stable_id', parent);
      const { data, error } = await query.order('name');
      if (error) throw error;
      return (data ?? []) as PickerFolder[];
    },
  });
}

/**
 * Link an entity to a folder. The database checks the folder is in the latest
 * scan of an approved root, and records the scan's runtime and the folder's
 * names below the root; no path is sent or stored.
 */
export async function linkFolder(kind: BindableKind, entityId: string, root: ConnectorRoot, folderStableId: string): Promise<void> {
  const { error } = await supabase.from('folder_bindings').insert({
    connector_id: root.connector_id,
    entity_kind: kind,
    entity_id: entityId,
    root_key: root.root_key,
    folder_stable_id: folderStableId,
  });
  if (error) {
    if (error.code === '23505') throw new Error('this already has a folder on that computer; unlink it first');
    throw error;
  }
}

/**
 * Link again: point the link at a folder in the latest scan (a repair after
 * the runtime changed), or at the same folder (confirming a folder that
 * changed since it was linked). The database checks it and records it afresh.
 */
export async function relinkFolder(bindingId: string, folderStableId: string): Promise<void> {
  const { data, error } = await supabase.from('folder_bindings').update({ folder_stable_id: folderStableId }).eq('id', bindingId).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('that link was removed');
}

/** Remove the link only: nothing on the computer or in Cerebro Diem changes. */
export async function unlinkFolder(bindingId: string): Promise<void> {
  const { data, error } = await supabase.from('folder_bindings').delete().eq('id', bindingId).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('that link was already removed');
}
